import { create } from "zustand";
import { send_request } from "../../utils/Request";

const useSchedulingStore = create((set, get) => ({
  scheduledMessages: [],
  scheduledMessagesLoading: false,
  recurringSchedules: [],
  recurringSchedulesLoading: false,

  scheduleUserMessage: async (chatSessionId, agentId, message, resumeAt) => {
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/schedule-message`,
        { agentId, message, resumeAt },
        {},
        "POST"
      );
      const data = response?.data;
      if (data) {
        set((s) => ({
          scheduledMessages: [...s.scheduledMessages, data],
        }));
      }
      return data;
    } catch (e) {
      set({ error: e.message || "Failed to schedule message" });
      return null;
    }
  },

  fetchScheduledMessagesBySession: async (chatSessionId) => {
    set({ scheduledMessagesLoading: true });
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/scheduled-messages`,
        "",
        {},
        "GET"
      );
      set({
        scheduledMessages: response?.data || [],
        scheduledMessagesLoading: false,
      });
    } catch (e) {
      set({
        error: e.message || "Failed to load scheduled messages",
        scheduledMessagesLoading: false,
      });
    }
  },

  fetchScheduledMessagesByAgent: async (agentId) => {
    set({ scheduledMessagesLoading: true });
    try {
      const response = await send_request(
        `project-service/agent/${agentId}/scheduled-messages`,
        "",
        {},
        "GET"
      );
      set({
        scheduledMessages: response?.data || [],
        scheduledMessagesLoading: false,
      });
    } catch (e) {
      set({
        error: e.message || "Failed to load scheduled messages",
        scheduledMessagesLoading: false,
      });
    }
  },

  editScheduledMessage: async (scheduleId, payload) => {
    try {
      const response = await send_request(
        `project-service/agent/scheduled-message/${scheduleId}`,
        payload,
        {},
        "PUT"
      );
      if (response?.data) {
        set((s) => {
          const scheduledMessages = [...s.scheduledMessages];
          const idx = scheduledMessages.findIndex(
            (sm) => sm.scheduleId === scheduleId
          );
          if (idx >= 0) scheduledMessages[idx] = response.data;
          return { scheduledMessages };
        });
      }
      return response?.data;
    } catch (e) {
      set({ error: e.message || "Failed to edit scheduled message" });
      return null;
    }
  },

  cancelScheduledMessage: async (scheduleId) => {
    try {
      const response = await send_request(
        `project-service/agent/scheduled-message/${scheduleId}/cancel`,
        {},
        {},
        "POST"
      );
      if (response?.data) {
        set((s) => {
          const scheduledMessages = [...s.scheduledMessages];
          const idx = scheduledMessages.findIndex(
            (sm) => sm.scheduleId === scheduleId
          );
          if (idx >= 0) {
            scheduledMessages[idx] = {
              ...scheduledMessages[idx],
              status: response.data.status || "CANCELLED",
            };
          }
          return { scheduledMessages };
        });
      }
      return response?.data;
    } catch (e) {
      set({ error: e.message || "Failed to cancel scheduled message" });
      return null;
    }
  },

  // ── Recurring Schedules ──

  fetchRecurringSchedulesBySession: async (chatSessionId) => {
    set({ recurringSchedulesLoading: true });
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/recurring-schedules`,
        "",
        {},
        "GET"
      );
      set({
        recurringSchedules: response?.data || [],
        recurringSchedulesLoading: false,
      });
    } catch (e) {
      set({
        error: e.message || "Failed to load recurring schedules",
        recurringSchedulesLoading: false,
      });
    }
  },

  fetchRecurringSchedulesByAgent: async (agentId) => {
    set({ recurringSchedulesLoading: true });
    try {
      const response = await send_request(
        `project-service/agent/${agentId}/recurring-schedules`,
        "",
        {},
        "GET"
      );
      set({
        recurringSchedules: response?.data || [],
        recurringSchedulesLoading: false,
      });
    } catch (e) {
      set({
        error: e.message || "Failed to load recurring schedules",
        recurringSchedulesLoading: false,
      });
    }
  },

  editRecurringSchedule: async (scheduleId, payload) => {
    try {
      const response = await send_request(
        `project-service/agent/recurring-schedule/${scheduleId}`,
        payload,
        {},
        "PUT"
      );
      if (response?.data) {
        set((s) => {
          const recurringSchedules = [...s.recurringSchedules];
          const idx = recurringSchedules.findIndex(
            (rs) => rs.scheduleId === scheduleId
          );
          if (idx >= 0) recurringSchedules[idx] = response.data;
          return { recurringSchedules };
        });
      }
      return response?.data;
    } catch (e) {
      set({ error: e.message || "Failed to edit recurring schedule" });
      return null;
    }
  },

  cancelRecurringSchedule: async (scheduleId) => {
    try {
      const response = await send_request(
        `project-service/agent/recurring-schedule/${scheduleId}/cancel`,
        {},
        {},
        "POST"
      );
      if (response?.data) {
        set((s) => {
          const recurringSchedules = [...s.recurringSchedules];
          const idx = recurringSchedules.findIndex(
            (rs) => rs.scheduleId === scheduleId
          );
          if (idx >= 0) {
            recurringSchedules[idx] = {
              ...recurringSchedules[idx],
              status: response.data.status || "CANCELLED",
            };
          }
          return { recurringSchedules };
        });
      }
      return response?.data;
    } catch (e) {
      set({ error: e.message || "Failed to cancel recurring schedule" });
      return null;
    }
  },

  pauseRecurringSchedule: async (scheduleId) => {
    try {
      const response = await send_request(
        `project-service/agent/recurring-schedule/${scheduleId}/pause`,
        {},
        {},
        "POST"
      );
      if (response?.data) {
        set((s) => {
          const recurringSchedules = [...s.recurringSchedules];
          const idx = recurringSchedules.findIndex(
            (rs) => rs.scheduleId === scheduleId
          );
          if (idx >= 0) {
            recurringSchedules[idx] = {
              ...recurringSchedules[idx],
              status: response.data.status || "PAUSED",
            };
          }
          return { recurringSchedules };
        });
      }
      return response?.data;
    } catch (e) {
      set({ error: e.message || "Failed to pause recurring schedule" });
      return null;
    }
  },

  resumeRecurringSchedule: async (scheduleId) => {
    try {
      const response = await send_request(
        `project-service/agent/recurring-schedule/${scheduleId}/resume`,
        {},
        {},
        "POST"
      );
      if (response?.data) {
        set((s) => {
          const recurringSchedules = [...s.recurringSchedules];
          const idx = recurringSchedules.findIndex(
            (rs) => rs.scheduleId === scheduleId
          );
          if (idx >= 0) {
            recurringSchedules[idx] = {
              ...recurringSchedules[idx],
              status: response.data.status || "ACTIVE",
              nextResumeAt: response.data.nextResumeAt,
            };
          }
          return { recurringSchedules };
        });
      }
      return response?.data;
    } catch (e) {
      set({ error: e.message || "Failed to resume recurring schedule" });
      return null;
    }
  },
}));

export default useSchedulingStore;
